import React, { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Box,
  ScrollArea,
  Flex,
  ActionIcon,
  Card,
  Paper,
  InputWrapper,
  Input,
  Loader,
  Group,
} from "@mantine/core";
import useCoursesStore from "../../store/useCoursesStore"; 
import { IconPlus } from "../../shared/svg/AddIcon";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import { CheckedIcon } from "../../shared/svg";
import { LearningCourseData } from "../../types/api";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "../../shared/svg/SearchIcon";
import { InfoTooltip } from "../../shared/components/InfoTooltip";

const SearchBar = ({ onSearch }: { onSearch: (query: string) => void }) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleSearchClick = async () => {
    setLoading(true);
    await onSearch(searchQuery);
    setLoading(false);
    removeFocus();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchClick();
      removeFocus();
    }
  };

  const removeFocus = () => {
    const inputElement = document.querySelector("input") as HTMLElement;
    if (inputElement) {
      inputElement.blur();
    }
  };

  return (
    <InputWrapper
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10 }}
    >
      <Input
        value={searchQuery}
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        placeholder={t("search.placeholder")}
        radius="md"
        rightSection={
          <>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={async () => {
                if (searchQuery.trim() !== "") {
                  setLoading(true);

                  setSearchQuery("");
                  await onSearch("");
                  setLoading(false);
                }
              }}
              aria-label="Clear search"
              style={{ pointerEvents: "all" }}
            >
              <ClearIcon />
            </ActionIcon>
          </>
        }
        style={{
          borderRadius: "8px",
          borderColor: "#e0e0e0",
          fontSize: "16px",
          width: "100%",
        }}
      />
      {loading ? (
        <Loader size={40} color="rgba(111, 64, 226, 0.6)" />
      ) : (
        <ActionIcon
          onClick={handleSearchClick}
          variant="subtle"
          style={{
            borderRadius: "50%",
            cursor: "pointer",
            pointerEvents: "all",
            width: 42,
            height: 42,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            border: "1px solid rgb(183, 183, 183)",
          }}
        >
          <SearchIcon />
        </ActionIcon>
      )}
    </InputWrapper>
  );
};
interface Props {
  handleResourceLink: (links: { learning_course_id: number }[]) => void;
  relatedCourses?: LearningCourseData[]; // Масив курсів
}

const ModalWithSearchBar: React.FC<Props> = ({
  handleResourceLink,
  relatedCourses = [],
}) => {
  const { t } = useTranslation();
  const [isSearchVisible, setIsSearchVisible] = useState<boolean>(false);
  const [filteredCourses, setFilteredCourses] = useState<LearningCourseData[]>(
    []
  );
  const [selectedCourses, setSelectedCourses] =
    useState<LearningCourseData[]>(relatedCourses);
  const { learningCourses, fetchLearningCourses } = useCoursesStore();

  useEffect(() => {
    if (learningCourses?.results) {
      setFilteredCourses(learningCourses.results);
    }
  }, [learningCourses]);

  useEffect(() => {
    if (learningCourses.results.length === 0) fetchLearningCourses();
  }, []);

  const handleSearch = async (query: string) => {
    try {
      await fetchLearningCourses({ search: query });
    } catch (err) {
      console.log(err);
    }
  };

  const handleCourseSelect = (course: LearningCourseData) => {
    setSelectedCourses((prevSelectedCourses) => {
      const isAlreadySelected = prevSelectedCourses.some(
        (selectedCourse) => selectedCourse.id === course.id
      );
      if (isAlreadySelected) {
        return prevSelectedCourses.filter(
          (selectedCourse) => selectedCourse.id !== course.id
        );
      } else {
        return [...prevSelectedCourses, course];
      }
    });
  };

  const handleCourseRemove = (course: LearningCourseData) => {
    setSelectedCourses((prevSelectedCourses) =>
      prevSelectedCourses.filter(
        (selectedCourse) => selectedCourse.id !== course.id
      )
    );
  };

  useEffect(() => {
    const resourceLinks = selectedCourses.map((course) => ({
      learning_course_id: course.id!,
    }));
    handleResourceLink(resourceLinks);
  }, [selectedCourses, handleResourceLink]);

  return (
    <Box>
      <Flex align="center" gap={10}>
        <Group gap={0} align="center" wrap="nowrap">
          <Text size="md" fw={600} style={{ color: "#333" }}>
            {t("modal.relatedCourses")}
          </Text>
          <InfoTooltip
            label={t("goal_related_courses_description")}
            size={20}
          />
        </Group>

        <ActionIcon
          variant="subtle"
          onClick={() => setIsSearchVisible(!isSearchVisible)}
          style={{
            backgroundColor: "#f0f0f0",
            padding: "8px",
            borderRadius: "50%",
            boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          {!isSearchVisible ? <IconPlus /> : <ClearIcon />}
        </ActionIcon>
      </Flex>

      {/* Пошук і відображення результатів */}
      {isSearchVisible && (
        <Box
          mt="lg"
          style={{
            border: "1px solid #e0e0e0",
            padding: "16px",
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
          }}
        >
          <Stack gap="sm">
            <SearchBar onSearch={handleSearch} />

            <ScrollArea style={{ maxHeight: 250, overflowY: "auto" }}>
              <Stack gap={2} style={{ width: "100%" }}>
                {filteredCourses.length > 0 ? (
                  filteredCourses.map((learningCourse) => (
                    <Card
                      key={learningCourse.id}
                      radius="md"
                      withBorder
                      style={{
                        cursor: "pointer",
                        width: "100%",
                        margin: 0,
                        padding: "10px 14px",
                        backgroundColor: selectedCourses.some(
                          (course) => course.id === learningCourse.id
                        )
                          ? "rgba(111, 64, 226, 0.12)"
                          : "white",
                        transition: "background-color 0.2s ease",
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        gap: 10,
                      }}
                      onClick={() => handleCourseSelect(learningCourse)}
                    >
                      {selectedCourses.some(
                        (course) => course.id === learningCourse.id
                      ) && <CheckedIcon />}
                      <Text size="md" fw={500}>
                        {learningCourse.course.title}
                      </Text>
                    </Card>
                  ))
                ) : (
                  <Text>{t("modal.noCoursesFound")}</Text>
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        </Box>
      )}
      {selectedCourses.length > 0 && (
        <Box mt="lg" style={{ maxWidth: "100%", margin: "0 auto" }}>
          <Stack gap="sm">
            {selectedCourses.map((course) => (
              <Paper
                key={course.id}
                radius="md"
                p="sm"
                withBorder
                style={{
                  backgroundColor: "#f9f9f9",
                  borderColor: "#dee2e6",
                }}
              >
                <Flex align="center" justify="space-between" gap="sm">
                  <Text
                    size="sm"
                    fw={500}
                    style={{ maxWidth: "100%", color: "#212529" }}
                    truncate="end"
                  >
                    {course.course.title}
                  </Text>
                  <ActionIcon
                    onClick={() => handleCourseRemove(course)}
                    variant="subtle"
                    radius="xl"
                    size="sm"
                    color="gray"
                  >
                    <ClearIcon />
                  </ActionIcon>
                </Flex>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default ModalWithSearchBar;
